// src/components/Header.js

import React, { useState, useRef, useEffect } from 'react';
import { Navbar, Nav, Container } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import logo from '../assets/svg/gptw_logo.svg';
import ProfileDropdown from './ProfileDropdown';
import { selectUserData } from '../features/userSlice';
import { selectUnits } from '../features/unitsSlice';
import './Header.css';

const Header = () => {
  const headerRef = useRef(null);
  const location = useLocation();

  // Access user data from Redux store
  const userData = useSelector(selectUserData);
  const userRoleId = userData?.role?.id;

  // Access units data from the consolidated unitsSlice (no separate fetch needed)
  // Units are already fetched in Layout.js
  const units = useSelector(selectUnits);

  // Determine if the "Dashboard" link should be visible
  const showDashboard = units?.some((unit) => unit.visibility_level === 0 || unit.visibility_level === 2);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (userRoleId !== null && userRoleId !== undefined) {
      setIsVisible(![3, 4].includes(userRoleId));
    }
  }, [userRoleId]);

  const updateBodyPadding = () => {
    if (headerRef.current) {
      document.body.style.paddingTop = `${headerRef.current.offsetHeight}px`;
    }
  };

  useEffect(() => {
    // Set the initial padding when Header mounts
    updateBodyPadding();

    // Cleanup function to remove padding when Header unmounts
    return () => {
      document.body.style.paddingTop = '0';
    };
  }, []);

  useEffect(() => {
    // Update padding on window resize
    window.addEventListener('resize', updateBodyPadding);
    return () => {
      window.removeEventListener('resize', updateBodyPadding);
      // Ensure padding is removed if Header unmounts during a resize
      document.body.style.paddingTop = '0';
    };
  }, []);

  // Helper function to check if a path is active (supports nested routes)
  const isActive = (path) => {
    if (path === '/home') {
      // Exact match for home
      return location.pathname === path;
    }
    // For other routes, check if the current path starts with the nav path
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <header className="header-wrapper" ref={headerRef}>
      {/* Red top bar */}
      <div className="header-top-bar" />
      
      <Navbar expand="lg" className="header-navbar">
        <Container className="d-flex align-items-center">
          {/* Logo */}
          <Navbar.Brand href="/home" className="header-logo-link">
            <img src={logo} alt="Great Place To Work Logo" className="header-logo" />
          </Navbar.Brand>

          {/* Mobile Toggle */}
          <Navbar.Toggle aria-controls="header-navbar-nav" className="header-navbar-toggler" />

          {/* Navigation */}
          <Navbar.Collapse id="header-navbar-nav" className="header-navbar-collapse">
            <Nav className="header-nav">
              <Nav.Link 
                href="/home" 
                className={`header-nav-link ${isActive('/home') ? 'active' : ''}`}
              >
                Home
              </Nav.Link>
              <Nav.Link 
                href="/actionboard" 
                className={`header-nav-link ${isActive('/actionboard') ? 'active' : ''}`}
              >
                Actionboard
              </Nav.Link>
              <Nav.Link 
                href="/checkpoints" 
                className={`header-nav-link ${isActive('/checkpoints') ? 'active' : ''}`}
              >
                Checkpoints
              </Nav.Link>
              {showDashboard && (
                <Nav.Link 
                  href="/dashboard" 
                  className={`header-nav-link ${isActive('/dashboard') ? 'active' : ''}`}
                >
                  Dashboard
                </Nav.Link>
              )}
              {isVisible && (
                <Nav.Link 
                  href="/user_management" 
                  className={`header-nav-link ${isActive('/user_management') ? 'active' : ''}`}
                >
                  Projektsteuerung
                </Nav.Link>
              )}
            </Nav>
          </Navbar.Collapse>

          {/* Profile Dropdown */}
          <div className="header-profile-section">
            <ProfileDropdown />
          </div>
        </Container>
      </Navbar>
    </header>
  );
};

export default React.memo(Header);
